import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function PayPalReturn() {
  const [params] = useSearchParams();
  const orderId = params.get("token"); // PayPal sends order id as `token` in return URL
  const bookingId = params.get("booking_id");
  const [status, setStatus] = useState("capturing"); // capturing | success | failed
  const navigate = useNavigate();

  useEffect(() => {
    if (!orderId) {
      setStatus("failed");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post(`/payments/paypal/capture/${orderId}`);
        setStatus(data.payment_status === "paid" ? "success" : "failed");
      } catch {
        setStatus("failed");
      }
    })();
  }, [orderId]);

  return (
    <div data-testid="paypal-return-page" className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-2xl mx-auto px-6 py-16">
        <Card className="p-10 rounded-3xl border border-stone-200 bg-white text-center">
          {status === "capturing" && (
            <>
              <Loader2 className="w-14 h-14 text-pink-500 animate-spin mx-auto" />
              <h1 className="mt-6 font-heading text-3xl font-black tracking-tighter text-stone-900">Confirming your PayPal payment…</h1>
              <p className="text-stone-600 mt-2">This should only take a moment.</p>
            </>
          )}
          {status === "success" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" />
              <h1 className="mt-6 font-heading text-4xl font-black tracking-tighter text-stone-900">Payment successful!</h1>
              <p className="text-stone-600 mt-3">Your PayPal payment was captured. Your pickup is confirmed.</p>
              <div className="mt-8 flex gap-3 justify-center">
                {bookingId && (
                  <Link to={`/booking/${bookingId}`}>
                    <Button data-testid="view-booking-btn" className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6">View booking</Button>
                  </Link>
                )}
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6">Dashboard</Button>
              </div>
            </>
          )}
          {status === "failed" && (
            <>
              <XCircle className="w-16 h-16 text-red-500 mx-auto" />
              <h1 className="mt-6 font-heading text-3xl font-black tracking-tighter text-stone-900">Payment issue</h1>
              <p className="text-stone-600 mt-3">We couldn&rsquo;t confirm your PayPal payment. Please try again or contact support.</p>
              <div className="mt-8 flex gap-3 justify-center">
                {bookingId && (
                  <Link to={`/booking/${bookingId}`}>
                    <Button className="rounded-full bg-pink-500 hover:bg-pink-600 text-white font-bold h-12 px-6">Back to booking</Button>
                  </Link>
                )}
                <Button variant="outline" onClick={() => navigate("/dashboard")} className="rounded-full h-12 px-6">Dashboard</Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
